import React, { useEffect, useRef, useState, useCallback } from "react";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box, Button } from "@mui/material";
import { IWhatsAppEmbeddedSignupProps } from "./models";
import { useSearchParams } from "react-router-dom";

const APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "";
const CONFIG_ID = process.env.REACT_APP_FACEBOOK_CONFIG_ID || "";
const API_VERSION = process.env.REACT_APP_FACEBOOK_API_VERSION || "";

const WhatsAppEmbeddedSignup: React.FC<IWhatsAppEmbeddedSignupProps> = ({
  onSignupComplete,
  onSignupCancel,
  onSignupError,
  onLoginComplete,
}) => {
  const fbRootRef = useRef<HTMLDivElement | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [sdkResponse, setSdkResponse] = useState<FB.LoginResponse | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let fbRoot = document.getElementById("fb-root");
    if (!fbRoot) {
      fbRoot = document.createElement("div");
      fbRoot.id = "fb-root";
      document.body.appendChild(fbRoot);
    }

    fbRootRef.current = fbRoot as HTMLDivElement; //
    console.log(API_VERSION);
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: API_VERSION,
      });
      setSdkLoaded(true);
    };

    // Previne que o script seja carregado múltiplas vezes em desenvolvimento
    if (
      !document.querySelector(
        `script[src="https://connect.facebook.net/en_US/sdk.js"]`
      )
    ) {
      const script = document.createElement("script");
      script.src = `https://connect.facebook.net/en_US/sdk.js`;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
    }

    return () => {
      // Limpeza se o componente for desmontado
    };
  }, []);

  // 2. Listener para mensagens do Embedded Signup (postMessage)
  useEffect(() => {
    const messageListener = (event: any) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH") {
            const { phone_number_id, waba_id } = data.data;
            setSessionInfo(data);
            if (onSignupComplete) {
              onSignupComplete({
                phoneNumberId: phone_number_id,
                whatsAppBusinessAccountId: waba_id,
              });
            }
          } else if (data.event === "CANCEL") {
            const { current_step } = data.data;
            setSessionInfo(data);
            if (onSignupCancel) {
              onSignupCancel({ current_step });
            }
          } else if (data.event === "ERROR") {
            const { error_message } = data.data;
            setSessionInfo(data);
            if (onSignupError) {
              onSignupError({ error_message });
            }
          }
        } else {
          console.log("Non WA_EMBEDDED_SIGNUP message:", data);
        }
      } catch (error) {
        console.warn("Non JSON Responses or parsing error:", event.data, error);
      }
    };

    window.addEventListener("message", messageListener);

    return () => {
      window.removeEventListener("message", messageListener);
    };
  }, [onSignupComplete, onSignupCancel, onSignupError]); // Dependências para o useCallback

  // 3. Callback do FB.login()
  const fbLoginCallback = useCallback((response: any) => {
    console.log("Facebook Login SDK Response:", response);
    setSdkResponse(response);

    if (response.authResponse) {
      const code = response.authResponse.code;
      if (onLoginComplete) {
        onLoginComplete({ authorizationCode: code });
      }
    } else {
      console.warn("Facebook Login canceled or failed.");
    }
  }, []);

  // 4. Função para iniciar o fluxo
  const launchWhatsAppSignup = () => {
    if (!sdkLoaded) {
      alert("Facebook SDK ainda não carregado. Por favor, aguarde.");
      return;
    }
    if (!window.FB) {
      alert("Objeto FB não disponível. Verifique o carregamento do SDK.");
      return;
    }
    window.FB.login(fbLoginCallback, {
      config_id: CONFIG_ID,
      response_type: "code",
      override_default_response_type: true,
      extras: { version: "v3" }, // ou a versão mais recente que a Meta indicar
    });
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h1>Conecte seu WhatsApp Business API</h1>
      <p>
        Clique no botão abaixo para iniciar o processo de conexão do seu número
        de WhatsApp com a nossa plataforma via Facebook.
      </p>
      <Button
        type="button"
        onClick={launchWhatsAppSignup}
        disabled={!sdkLoaded}
        style={{
          backgroundColor: "#1877f2",
          border: 0,
          borderRadius: "4px",
          color: "#fff",
          cursor: "pointer",
          fontSize: "16px",
          fontWeight: "bold",
          height: "40px",
          padding: "0 24px",
        }}
      >
        {sdkLoaded ? (
          <Box>
            <FacebookIcon fontSize="small" />
            <WhatsAppIcon fontSize="small" />
            Conectar WhatsApp Business
          </Box>
        ) : (
          "Carregando..."
        )}
      </Button>
      {sessionInfo && searchParams.get("debug") && (
        <>
          <h3>Informações da Sessão (Embedded Signup):</h3>
          <pre
            style={{
              backgroundColor: "#f8f8f8",
              border: "1px solid #ddd",
              padding: "10px",
              borderRadius: "4px",
              overflowX: "auto",
            }}
          >
            {JSON.stringify(sessionInfo, null, 2)}
          </pre>
        </>
      )}

      {sdkResponse && searchParams.get("debug") && (
        <>
          <h3>Resposta do SDK (Facebook Login):</h3>
          <pre
            style={{
              backgroundColor: "#f8f8f8",
              border: "1px solid #ddd",
              padding: "10px",
              borderRadius: "4px",
              overflowX: "auto",
            }}
          >
            {JSON.stringify(sdkResponse, null, 2)}
          </pre>
        </>
      )}

      {!sdkLoaded && <p>Carregando SDK do Facebook...</p>}
    </div>
  );
};

export default WhatsAppEmbeddedSignup;
